import React, { useEffect, useState } from 'react'
import { Button, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { loadActiveVerificationConfig, saveActiveVerificationConfig, toggleUpdateVerificationConfigDialog, updateVerificationConfig } from './edit-project-actions';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ConfirmDialog from '../../template/ConfirmDialog';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const DocumentVerificationMethod = () => {

    const { project } = useParams()
    const { activeVerificationConfig } = useSelector(state => state.pages.editProject)

    useEffect(() => {
        loadActiveVerificationConfig(project)
    }, [])


    const validateJKSFile = (base64Content, isPrivate) => {
        try {
            // Decodificar el contenido base64
            const binaryString = atob(base64Content);
            const bytes = new Uint8Array(binaryString.length);
            
            // Convertir a array de bytes
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // Verificar el magic number de JKS (0xFEEDFEED)
            if (bytes[0] === 0xFE && bytes[1] === 0xED && bytes[2] === 0xFE && bytes[3] === 0xED) {
                // Verificar la versión (2)
                if (bytes[4] === 0x00 && bytes[5] === 0x00 && bytes[6] === 0x00 && bytes[7] === 0x02) {
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error("Error validando archivo JKS:", error);
            return false;
        }
    }

    const handleFileUpload = async (event, keyType) => {
        const file = event.target.files[0];
        if (file) {
            // Validar la extensión del archivo
            if (!file.name.toLowerCase().endsWith('.jks')) {
                updateVerificationConfig({
                    target: {
                        name: `${keyType}Error`,
                        value: 'The file must have a .jks extension'
                    }
                });
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const base64Content = e.target.result.split(',')[1];

                // Validar el formato de la clave
                const isValid = validateJKSFile(base64Content, keyType === 'privateKey');

                if (isValid) {
                    updateVerificationConfig({
                        target: {
                            name: keyType,
                            value: base64Content
                        }
                    });
                    updateVerificationConfig({
                        target: {
                            name: `${keyType}Error`,
                            value: null
                        }
                    });
                } else {
                    updateVerificationConfig({
                        target: {
                            name: `${keyType}Error`,
                            value: `The file is not a valid keystore in JKS format`
                        }
                    });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <form onSubmit={(e) => saveActiveVerificationConfig(e, project) }>
            <div className="grid gap-4 p-4">
                <p className="text-md border-b pb-4 text-gray-800">Define the document verification and integrity method to validate the authenticity of the documents and prevent them from being modified.</p>
                <div className="grid gap-4">
                    <div className="flex flex-row items-center gap-4">

                        <FormControl variant="outlined" fullWidth={false} className="w-[300px]" >
                            <InputLabel htmlFor="verificationMethod" required={true}>Select verification method</InputLabel>
                            <Select
                                id="verificationMethod"
                                name="verificationMethod"
                                fullWidth={false}
                                label="Select verification method"
                                value={activeVerificationConfig?.verificationMethod}
                                onChange={updateVerificationConfig}
                            >
                                <MenuItem key="NONE" value="NONE"><em>NONE</em></MenuItem>
                                <MenuItem key="SHA256" value="SHA256">SHA256</MenuItem>
                                <MenuItem key="HMAC" value="HMAC">HMAC</MenuItem>
                                <MenuItem key="ECDSA" value="ECDSA">ECDSA</MenuItem>
                            </Select>
                        </FormControl>

                        <Tooltip title="NONE: No verification is performed. HMAC: Uses a secret key to generate and verify signatures, good for simple authentication. ECDSA: Public key cryptography that provides stronger security and non-repudiation. SHA256: One-way hash function that creates document fingerprints but doesn't verify authenticity.">
                            <HelpOutlineIcon />
                        </Tooltip>
                    </div>
                    {activeVerificationConfig?.verificationMethod === "HMAC" && (
                        <div>
                            <TextField
                                label="Secret key"
                                name="secret"
                                id="secret"
                                required
                                type={activeVerificationConfig?.showPassword ? "text" : "password"}
                                className="w-[300px]"
                                value={activeVerificationConfig?.secret}
                                onChange={updateVerificationConfig}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onMouseDown={() => updateVerificationConfig({ target: { name: "showPassword", value: true } })}
                                            onMouseUp={() => updateVerificationConfig({ target: { name: "showPassword", value: false } })}
                                            onMouseLeave={() => updateVerificationConfig({ target: { name: "showPassword", value: false } })}
                                        >
                                            {activeVerificationConfig?.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </IconButton>
                                    )
                                }}
                            />
                        </div>
                    )}
                    {activeVerificationConfig?.verificationMethod === "ECDSA" && (
                        <div className="grid gap-4">
                            <div className="flex gap-4 items-center">
                                <Button
                                    component="label"
                                    role={undefined}
                                    variant="contained"
                                    tabIndex={-1}
                                    type="file"
                                    name="keystoreBase64"
                                    className="h-12 w-[300px]"
                                    startIcon={<UploadFileRoundedIcon />}
                                    onClick={() => document.getElementById("keystoreBase64").click()}
                                >
                                    Upload JKS Keystore File
                                </Button>
                                <input
                                    type="file"
                                    id="keystoreBase64"
                                    name="keystoreBase64"
                                    hidden
                                    accept=".jks"
                                    onChange={(e) => handleFileUpload(e, 'keystoreBase64')}
                                />
                                <span>
                                    {activeVerificationConfig?.keystoreBase64Error  ? (
                                        <span className="flex gap-2 items-center text-red-500">
                                            <ErrorOutlineIcon /> {activeVerificationConfig?.keystoreBase64Error}
                                        </span>
                                    ) : activeVerificationConfig?.keystoreBase64 ? (
                                        <span className="flex gap-2 items-center">
                                            <CheckCircleRoundedIcon className="text-green-500" /> Keystore uploaded
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                            <TextField
                                required
                                label="Keystore Password"
                                name="keystorePassword"
                                id="keystorePassword"
                                type={activeVerificationConfig?.showPassword ? "text" : "password"}
                                className="w-[300px]"
                                value={activeVerificationConfig?.keystorePassword}
                                onChange={updateVerificationConfig}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            onMouseDown={() => updateVerificationConfig({ target: { name: "showPassword", value: true } })}
                                            onMouseUp={() => updateVerificationConfig({ target: { name: "showPassword", value: false } })}
                                            onMouseLeave={() => updateVerificationConfig({ target: { name: "showPassword", value: false } })}
                                        >
                                            {activeVerificationConfig?.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                        </IconButton>
                                    )
                                }}
                            />

                            <TextField
                                required
                                label="Key Alias"
                                name="keyAlias"
                                id="keyAlias"
                                className="w-[300px]"
                                value={activeVerificationConfig?.keyAlias}
                                onChange={updateVerificationConfig}
                            />
                            <div className="flex gap-4 items-center">

                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="p-4 text-right bg-gray-100 border-t border-gray-300">
                <Button variant="contained" type='submit' className="text-dark-500" >SAVE</Button>
            </div>
        </form>
    )
}

export default DocumentVerificationMethod;
